import { useState } from "react";
import { Link } from "react-router";
import { FilterChips } from "../components/FilterChips";
import { Pagination } from "../components/Pagination";
import { SearchInput } from "../components/SearchInput";
import { ServerPicker } from "../components/ServerPicker";
import {
  Alert,
  Card,
  EmptyState,
  PageHeader,
  SuspendedBadge,
  Table,
  TableSkeleton,
  Tag,
  Td,
  Th,
  cx,
} from "../components/ui";
import { formatNumber, formatRelative } from "../lib/format";
import { Icon } from "../components/icons";
import { useApps } from "../lib/queries";
import type { AppSource } from "../lib/types";

const LIMIT = 25;

/**
 * Named for what an operator is looking at rather than for the stored label.
 * `repo` and `github` are both GitHub to anybody reading this page; what
 * separates them is whether Maxlayer holds the connection, which is the thing
 * worth being able to filter on when a git integration misbehaves.
 */
const SOURCES: { value: AppSource; label: string }[] = [
  { value: "repo", label: "Connected repo" },
  { value: "github", label: "GitHub" },
  { value: "git", label: "Git URL" },
  { value: "docker", label: "Docker image" },
];

const sourceLabel = (source: string) =>
  SOURCES.find((entry) => entry.value === source)?.label ?? source;

/**
 * Every workload on the platform.
 *
 * Deliberately without a state column, and without a state filter.
 *
 * The stored `state` is written when an action is requested and never written
 * back, so a list built from it shows apps as `deploying` long after the build
 * finished. Resolving the real one costs two engine calls per row across every
 * server in the fleet, which is a price this page does not need to pay: the
 * detail page resolves it for the one app an operator is actually looking at.
 * Showing nothing beats showing a value we know to be wrong.
 *
 * Source is the opposite kind of value and is filterable for that reason — it
 * is decided when the app is created and never drifts, so filtering on it
 * answers the question it appears to answer.
 */
export function AppsPage() {
  const [q, setQ] = useState("");
  const [serverId, setServerId] = useState<string>("");
  const [source, setSource] = useState<AppSource | "">("");
  const [flagged, setFlagged] = useState(false);
  const [offset, setOffset] = useState(0);

  const { data, isPending, error } = useApps({
    q,
    limit: LIMIT,
    offset,
    ...(serverId ? { serverId } : {}),
    ...(source ? { source } : {}),
    ...(flagged ? { flagged } : {}),
  });

  // Any filter change returns to the first page. Page four of the old filter is
  // rarely page four of the new one, and is sometimes past the end of it.
  function reset<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setOffset(0);
    };
  }

  const filtered = Boolean(q || serverId || source || flagged);

  return (
    <>
      <PageHeader
        title="Apps"
        description="Every workload on the platform, across all tenants and servers."
      />

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
          <SearchInput value={q} onChange={reset(setQ)} placeholder="Search app or org…" />

          <div className="w-full sm:max-w-52">
            <ServerPicker
              value={serverId}
              onChange={reset(setServerId)}
              emptyLabel="All servers"
              ariaLabel="Filter by server"
            />
          </div>

          <FilterChips
            value={source}
            onChange={reset(setSource)}
            options={SOURCES}
            allLabel="All sources"
            ariaLabel="Filter by source"
          />

          {/*
            The queue the abuse screen leaves for a person: apps it allowed
            but whose name or source read like a web terminal, a desktop or a
            miner. A toggle rather than a chip in the source row, because it
            cuts across sources.
          */}
          <button
            type="button"
            aria-pressed={flagged}
            onClick={() => reset(setFlagged)(!flagged)}
            className={cx(
              "inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs transition-colors duration-150",
              flagged
                ? "border-warn/50 bg-warn/10 text-warn"
                : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
            )}
          >
            <Icon name="warning" className="size-3.5" />
            Flagged
          </button>

          {/* The count lives here rather than only in the pager, which hides
              itself on a single page — the answer to "how many are there" was
              then visible only when there were enough to need paging. */}
          {!isPending && !error && (
            <p className="ml-auto text-sm text-fg-subtle">
              {formatNumber(data.total)} {data.total === 1 ? "app" : "apps"}
              {filtered && " matching"}
            </p>
          )}
        </div>

        {isPending ? (
          <TableSkeleton columns={5} />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            title="No matching apps"
            description={filtered ? "Try relaxing the filters." : undefined}
          />
        ) : (
          <>
            <Table
              head={
                <tr>
                  <Th>App</Th>
                  <Th>Organization</Th>
                  {/* Source sits where the database list puts engine, so the
                      two workload pages read as one design. */}
                  <Th priority="sm">Source</Th>
                  <Th priority="md">Server</Th>
                  <Th priority="lg">Updated</Th>
                </tr>
              }
            >
              {data.data.map((app) => (
                <tr key={app.id} className="hover:bg-raised">
                  <Td>
                    <Link
                      to={`/apps/${app.id}`}
                      className="font-medium text-accent hover:underline"
                    >
                      {app.name}
                    </Link>
                    {app.abuseFlags.length > 0 && (
                      <span className="ml-2" title={`Matched: ${app.abuseFlags.join(", ")}`}>
                        <Tag tone="warn">{app.abuseFlags.join(" · ")}</Tag>
                      </span>
                    )}

                    {app.replicas > 1 && (
                      <span className="ml-2" title={app.autoscale ? `Auto-scale ${app.autoscale.min}–${app.autoscale.max} at ${app.autoscale.targetCpu}% CPU` : "Set by the customer"}>
                        <Tag>×{app.replicas}{app.autoscale ? " auto" : ""}</Tag>
                      </span>
                    )}
                  </Td>
                  <Td>
                    <Link
                      to={`/organizations/${app.organization.id}`}
                      className="hover:underline"
                    >
                      {app.organization.name}
                    </Link>
                    {app.organization.suspendedAt && (
                      <span className="ml-2">
                        <SuspendedBadge />
                      </span>
                    )}
                  </Td>
                  <Td priority="sm">
                    <Tag>{sourceLabel(app.source.type)}</Tag>
                  </Td>
                  <Td className="text-fg-subtle" priority="md">
                    {app.server?.name ?? "—"}
                  </Td>
                  <Td className="text-fg-subtle" priority="lg">
                    {formatRelative(app.updatedAt)}
                  </Td>
                </tr>
              ))}
            </Table>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>
    </>
  );
}
